import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Save, ArrowLeft, Upload, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { verseAPI, surahAPI } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { getErrorMessage } from '../../utils/errorHandler';

const BulkVerseUpload = () => {
  const { surahId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [surah, setSurah] = useState(null);
  const [turkishText, setTurkishText] = useState('');
  const [arabicText, setArabicText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentVerse, setCurrentVerse] = useState(0);
  const [totalVerses, setTotalVerses] = useState(0);

  React.useEffect(() => {
    loadSurah();
  }, [surahId]);

  const loadSurah = async () => {
    try {
      const response = await surahAPI.getOne(surahId);
      setSurah(response.data);
    } catch (error) {
      console.error('Error loading surah:', error);
      toast({
        title: 'Hata!',
        description: 'Sure bilgileri yuklenemedi.',
        variant: 'destructive'
      });
    }
  };

  // Rakamı superscript formatına çevir
  const toSuperscript = (num) => {
    const superscriptMap = {
      '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴',
      '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹'
    };
    return num.toString().split('').map(digit => superscriptMap[digit] || digit).join('');
  };

  // Satırı parse et - başında numara varsa ayıkla
  const parseVerseLine = (line) => {
    // Regex: "1." veya "2. 3. 4." gibi numaraları yakala
    const match = line.match(/^(\d+(?:\.\s*\d+)*)\.\s*(.+)$/);
    if (match) {
      const numbers = match[1].split('.').map(n => n.trim()).filter(n => n);
      
      // Superscript formatı: İlk rakam normal, diğerleri küçük
      let verseNumber;
      if (numbers.length === 1) {
        verseNumber = numbers[0]; // Tek ayet: "1"
      } else {
        // Birleşik ayet: "2³⁴" formatı
        verseNumber = numbers[0] + numbers.slice(1).map(n => toSuperscript(n)).join('');
      }
      
      const text = match[2].trim();
      return { verseNumber, text, hasNumber: true };
    }
    return { verseNumber: null, text: line.trim(), hasNumber: false };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const turkishLines = turkishText.split('\n').filter(line => line.trim() !== '');
    const arabicLines = arabicText.split('\n').filter(line => line.trim() !== '');

    // Her iki dilde de sadece numaralı satırları al
    const turkishVerses = turkishLines.map(line => parseVerseLine(line)).filter(v => v.hasNumber);
    const arabicVerses = arabicLines.map(line => parseVerseLine(line)).filter(v => v.hasNumber);

    // Kontrol: Ayet sayıları eşit mi?
    if (turkishVerses.length !== arabicVerses.length) {
      toast({
        title: '⚠️ Uyarı!',
        description: `Turkce ve Arapca ayet sayilari esit olmali! Turkce: ${turkishVerses.length}, Arapca: ${arabicVerses.length}`,
        variant: 'destructive',
        duration: 7000
      });
      return;
    }

    if (turkishVerses.length === 0) {
      toast({
        title: '⚠️ Uyarı!',
        description: 'En az 1 ayet girmelisiniz! (Baslangicta rakam olmali: 1., 2. 3. 4. gibi)',
        variant: 'destructive'
      });
      return;
    }

    setIsLoading(true);
    setTotalVerses(turkishVerses.length);
    setCurrentVerse(0);
    setUploadProgress(0);

    try {
      let successCount = 0;
      let errorCount = 0;
      const errors = [];

      // Her ayeti kaydet
      for (let i = 0; i < turkishVerses.length; i++) {
        setCurrentVerse(i + 1);
        const progress = Math.round(((i + 1) / turkishVerses.length) * 100);
        setUploadProgress(progress);

        const verseData = {
          surahId: surahId,
          verseNumber: turkishVerses[i].verseNumber, // "1" veya "2-3-4" formatında
          textArabic: arabicVerses[i].text,
          textTurkish: turkishVerses[i].text,
          translation: turkishVerses[i].text,
          order: i + 1,
          audioStartTime: 0,
          audioEndTime: 0
        };

        try {
          await verseAPI.create(verseData);
          successCount++;
        } catch (error) {
          errorCount++;
          errors.push(`Ayet ${turkishVerses[i].verseNumber}: ${getErrorMessage(error, 'Hata')}`);
        }
      }

      // Sonuç mesajı
      if (errorCount === 0) {
        toast({
          title: '✅ Basarili!',
          description: `Toplam ${successCount} ayet basariyla eklendi!`,
          duration: 5000
        });
        navigate(`/admin/surahs/${surahId}/verses`);
      } else {
        toast({
          title: '⚠️ Kismen Basarili',
          description: `${successCount} ayet eklendi, ${errorCount} ayet hatali. ${errors.slice(0, 3).join(', ')}`,
          variant: 'destructive',
          duration: 10000
        });
      }
    } catch (error) {
      console.error('Error uploading verses:', error);
      toast({
        title: '❌ Hata!',
        description: getErrorMessage(error, 'Ayetler yuklenirken hata olustu.'),
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
      setUploadProgress(0);
      setCurrentVerse(0);
      setTotalVerses(0);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate(`/admin/surahs/${surahId}/verses`)}
          className="mb-4 text-[#6B5744] hover:bg-[#E8DCC8]"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Geri Don
        </Button>

        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-[#5C4033]">
              {surah ? `${surah.name} Suresi - Toplu Ayet Ekleme` : 'Toplu Ayet Ekleme'}
            </h2>
            <p className="text-[#8B7355] mt-1">
              Her satir bir ayet olarak kaydedilecektir. Turkce ve Arapca satir sayilari esit olmalidir.
            </p>
          </div>
        </div>
      </div>

      {/* Yönergeler */}
      <Card className="bg-blue-50 border-blue-200 p-4 mb-6">
        <div className="flex items-start space-x-3">
          <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-semibold text-blue-900 mb-2">Nasıl Kullanılır?</h3>
            <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
              <li><strong>Başında rakam olmalı:</strong> &quot;1.&quot;, &quot;2. 3. 4.&quot; gibi</li>
              <li>Türkçe mealleri alttaki alana yapıştırın (başında rakam olan satırlar ayet olarak algılanır)</li>
              <li>Arapça metinleri alttaki alana yapıştırın (başında rakam olan satırlar ayet olarak algılanır)</li>
              <li>Her iki alanda da ayet sayıları eşit olmalıdır!</li>
              <li>&quot;Kaydet&quot; butonuna tıklayın</li>
            </ol>
            <p className="text-xs text-blue-700 mt-2">
              💡 <strong>Örnek:</strong> &quot;2. 3. 4. Hamd Alemlerin Rabbi...&quot; → Ayet numarası: &quot;2-3-4&quot;
            </p>
          </div>
        </div>
      </Card>

      {/* Progress Bar */}
      {isLoading && (
        <Card className="bg-white border-[#E8DCC8] p-6 mb-6">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-[#5C4033]">
                Ayetler Kaydediliyor...
              </span>
              <span className="text-sm font-medium text-[#8B7355]">
                {currentVerse} / {totalVerses} ({uploadProgress}%)
              </span>
            </div>
            <div className="w-full bg-[#E8DCC8] rounded-full h-4 overflow-hidden">
              <div
                className="bg-[#8B7355] h-4 rounded-full transition-all duration-300 flex items-center justify-center"
                style={{ width: `${uploadProgress}%` }}
              >
                <span className="text-xs text-white font-semibold">{uploadProgress}%</span>
              </div>
            </div>
            <p className="text-xs text-[#8B7355] text-center">
              Lütfen bekleyin, ayetler veritabanına kaydediliyor...
            </p>
          </div>
        </Card>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Bismillah Ayarı */}
        <Card className="bg-orange-50 border-2 border-orange-300 p-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-[#5C4033] mb-1">
                  🕌 Bismillah Gösterimi
                </h3>
                <p className="text-sm text-[#8B7355]">
                  Sure başında Bismillahirrahmanirrahim yazısını göster/gizle
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={surah?.showBismillah !== false}
                  onChange={async (e) => {
                    const newValue = e.target.checked;
                    
                    // Önce UI'yi güncelle
                    setSurah(prev => ({...prev, showBismillah: newValue}));
                    
                    try {
                      // Sonra backend'i güncelle
                      await surahAPI.update(surahId, {
                        ...surah,
                        showBismillah: newValue
                      });
                      
                      toast({
                        title: "Başarılı",
                        description: newValue ? "Bismillah gösterimi aktif edildi" : "Bismillah gösterimi pasif edildi",
                        variant: "default"
                      });
                    } catch (error) {
                      // Hata olursa eski değere geri dön
                      setSurah(prev => ({...prev, showBismillah: !newValue}));
                      toast({
                        title: "Hata",
                        description: "Bismillah ayarı güncellenemedi",
                        variant: "destructive"
                      });
                    }
                  }}
                  className="sr-only peer"
                />
                <div className="w-14 h-7 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-orange-600"></div>
                <span className="ml-3 text-sm font-medium text-[#5C4033]">
                  {surah?.showBismillah !== false ? 'Aktif' : 'Pasif'}
                </span>
              </label>
            </div>
            
            {/* Önizleme */}
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-orange-200">
              <div className="bg-white p-4 rounded-lg border border-orange-200">
                <p className="text-xs text-[#8B7355] mb-2">Türkçe Önizleme:</p>
                <p className="text-lg font-semibold text-[#5C4033] text-center">
                  {surah?.showBismillah !== false ? 'Bismillahirrahmanirrahim' : '(Gösterilmeyecek)'}
                </p>
              </div>
              <div className="bg-white p-4 rounded-lg border border-orange-200">
                <p className="text-xs text-[#8B7355] mb-2">Arapça Önizleme:</p>
                <p className="text-2xl text-[#5C4033] text-center" style={{ fontFamily: '"Traditional Arabic", serif' }}>
                  {surah?.showBismillah !== false ? 'بِسْمِ اللّٰهِ الرَّحْمٰنِ الرَّحِيمِ' : '(لن يتم عرضه)'}
                </p>
              </div>
            </div>
          </div>
        </Card>

        <Card className="bg-white border-[#E8DCC8] p-6">
          <div className="space-y-6">
            {/* Türkçe Mealler */}
            <div className="space-y-2">
              <label className="text-lg font-semibold text-[#5C4033] flex items-center">
                📖 Türkçe Mealler
                <span className="ml-2 text-sm font-normal text-[#8B7355]">
                  (Başında rakam olan satırlar ayet olarak algılanır)
                </span>
              </label>
              
              <textarea
                value={turkishText}
                onChange={(e) => setTurkishText(e.target.value)}
                placeholder="1. Bismillahirrahmanirrahim&#10;2. 3. 4. Hamd Alemlerin Rabbi Rahman Rahim Hesap Mükafat Ve Ceza Gününün Ahiret Gününün Maliki Allaha Mahsustur&#10;5. Allahım Yalnız Sana İbadet Ederiz Ve Yalnız Senden Yardım Dileriz"
                className="min-h-[300px] w-full p-4 border border-[#D5C4A1] rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-[#8B7355] font-mono text-sm"
                rows={15}
              />
              <p className="text-xs text-[#8B7355]">
                💡 Başında rakam olan satırlar (1., 2. 3. 4., 5. gibi) ayet olarak algılanır
              </p>
            </div>

            {/* Arapça Metinler */}
            <div className="space-y-2">
              <label className="text-lg font-semibold text-[#5C4033] flex items-center">
                📜 Arapça Metinler
                <span className="ml-2 text-sm font-normal text-[#8B7355]">
                  (Başında rakam olan satırlar ayet olarak algılanır)
                </span>
              </label>
              
              <textarea
                value={arabicText}
                onChange={(e) => setArabicText(e.target.value)}
                placeholder="1. بسم الله الرحمن الرحيم&#10;2. 3. 4. الحمد لله رب العالمين الرحيم الرحيم صاحب يوم الحساب والثواب وعذاب اليوم الأخير&#10;5. اللهم نسجد لك وحدك ونطلب منك المساعدة وحدك"
                className="min-h-[300px] w-full p-4 border border-[#D5C4A1] rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-[#8B7355] text-right text-xl"
                style={{ fontFamily: '"Traditional Arabic", serif', direction: 'rtl' }}
                rows={15}
              />
              <p className="text-xs text-[#8B7355]">
                💡 Başında rakam olan satırlar (1., 2. 3. 4., 5. gibi) ayet olarak algılanır
              </p>
            </div>

            {/* Bilgi Mesajı */}
            <Card className="bg-green-50 border-green-200 p-3">
              <p className="text-sm text-green-800">
                ✅ <strong>Hazır!</strong> &quot;Toplu Kaydet&quot; butonuna tıklayarak ayetleri ekleyebilirsiniz.
              </p>
            </Card>
          </div>
        </Card>

        {/* Butonlar */}
        <div className="flex justify-end space-x-3">
          <Button
            type="button"
            variant="ghost"
            onClick={() => navigate(`/admin/surahs/${surahId}/verses`)}
            disabled={isLoading}
            className="text-[#6B5744]"
          >
            İptal
          </Button>
          <Button
            type="submit"
            disabled={isLoading}
            className="bg-[#8B7355] hover:bg-[#6B5744] text-white"
          >
            {isLoading ? (
              <>
                <Upload className="h-4 w-4 mr-2 animate-pulse" />
                Kaydediliyor...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Toplu Kaydet
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default BulkVerseUpload;
